import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LayoutItem } from 'react-grid-layout';

export type DashboardGridItem = Pick<LayoutItem, 'i' | 'x' | 'y' | 'w' | 'h'>;

export type WidgetDiffAction = 'delete' | 'hide' | 'rename' | 'drag' | 'resize';

export interface WidgetDiffEntry {
  type: WidgetDiffAction;
  widgetId: string;
  widgetBefore: Record<string, unknown>;
  layoutBefore: Array<{ i: string; x: number; y: number; w: number; h: number }>;
  widgetTitle: string;
  timestamp: number;
}

export interface DashboardEditSession {
  startedAt: string;
  lastChangeAt: string;
  changeCount: number;
}

export interface DashboardEditState {
  isDirty: boolean;
  isSaving: boolean;
  editSession: DashboardEditSession;
  unsavedChanges: DashboardGridItem[];
  arrangeMode: boolean;
  hiddenWidgets: string[];
  pendingRename: {
    widgetId: string;
    value: string;
  } | null;
  pendingUndo: {
    widgetId: string;
    previousKind: string;
    expiresAt: number;
  } | null;
  undoStack: WidgetDiffEntry[];
  redoStack: WidgetDiffEntry[];
}

const nowIso = () => new Date().toISOString();

const initialState: DashboardEditState = {
  isDirty: false,
  isSaving: false,
  editSession: {
    startedAt: nowIso(),
    lastChangeAt: nowIso(),
    changeCount: 0,
  },
  unsavedChanges: [],
  arrangeMode: false,
  hiddenWidgets: [],
  pendingRename: null,
  pendingUndo: null,
  undoStack: [],
  redoStack: [],
};

const dashboardEditSlice = createSlice({
  name: 'dashboardEdit',
  initialState,
  reducers: {
    startEditSession(state) {
      const now = nowIso();
      state.isDirty = false;
      state.isSaving = false;
      state.editSession = {
        startedAt: now,
        lastChangeAt: now,
        changeCount: 0,
      };
      state.unsavedChanges = [];
      state.arrangeMode = false;
      state.hiddenWidgets = [];
      state.pendingRename = null;
      state.pendingUndo = null;
      state.undoStack = [];
      state.redoStack = [];
    },
    markDirty(state, action: PayloadAction<DashboardGridItem[]>) {
      state.isDirty = true;
      state.editSession.lastChangeAt = nowIso();
      state.editSession.changeCount += 1;
      state.unsavedChanges = action.payload;
    },
    markClean(state) {
      state.isDirty = false;
      state.unsavedChanges = [];
    },
    setSaving(state, action: PayloadAction<boolean>) {
      state.isSaving = action.payload;
    },
    setArrangeMode(state, action: PayloadAction<boolean>) {
      state.arrangeMode = action.payload;
    },
    setHiddenWidgets(state, action: PayloadAction<string[]>) {
      state.hiddenWidgets = action.payload;
    },
    setPendingRename(
      state,
      action: PayloadAction<{ widgetId: string; value: string } | null>,
    ) {
      state.pendingRename = action.payload;
    },
    setPendingUndo(
      state,
      action: PayloadAction<{ widgetId: string; previousKind: string; expiresAt: number }>,
    ) {
      state.pendingUndo = action.payload;
    },
    clearPendingUndo(state) {
      state.pendingUndo = null;
    },
    pushUndo(state, action: PayloadAction<WidgetDiffEntry>) {
      state.redoStack = [];
      if (state.undoStack.length >= 20) state.undoStack.shift();
      state.undoStack.push(action.payload);
    },
    popUndo(state) { state.undoStack.pop(); },
    pushRedo(state, action: PayloadAction<WidgetDiffEntry>) {
      if (state.redoStack.length >= 20) state.redoStack.shift();
      state.redoStack.push(action.payload);
    },
    popRedo(state) { state.redoStack.pop(); },
    clearUndoRedoStacks(state) { state.undoStack = []; state.redoStack = []; },
    resetEditState() {
      return initialState;
    },
  },
});

export const {
  startEditSession,
  markDirty,
  markClean,
  setSaving,
  setArrangeMode,
  setHiddenWidgets,
  setPendingRename,
  setPendingUndo,
  clearPendingUndo,
  pushUndo,
  popUndo,
  pushRedo,
  popRedo,
  clearUndoRedoStacks,
  resetEditState,
} = dashboardEditSlice.actions;

export default dashboardEditSlice.reducer;
